import React, { useEffect, useRef, useState } from 'react';
import { buscarClientes } from '../../services/clienteService';
import { apellidoNombre } from '../../utils/nombrePersona';

/**
 * Buscar y vincular un cliente del sistema, o seguir sin cliente. Es el mismo patrón que ya
 * usa el bloque "¿Ya es cliente del estudio?" de ModalCrearTurno en
 * pages/Recepcion/TurneroPage.js (input con debounce de 350ms → buscarClientes → lista
 * clickeable → estado "vinculado" con botón Quitar), extraído acá como componente propio
 * porque en "Generar Documentos" hace falta además un gesto explícito para confirmar que se
 * sigue SIN cliente (acá eso decide si el documento sale en blanco o prellenado).
 *
 * `onElegirCliente(cliente | null)` se llama tanto al vincular un cliente como al desvincularlo
 * (con `null`). `onContinuar(cliente | null)` es el paso siguiente del flujo (abre el documento).
 *
 * `color` es el tono del trámite que abrió el modal (ver coloresTramite.js): tiñe el botón de
 * continuar y el recuadro del cliente vinculado, para que el paso de elegir cliente se sienta
 * parte de la misma tarjeta que lo disparó.
 */
export default function SelectorClienteOpcional({ onContinuar, color = '#2563eb' }) {
    const [busqueda, setBusqueda] = useState('');
    const [encontrados, setEncontrados] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [clienteVinculado, setClienteVinculado] = useState(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!busqueda.trim()) {
            setEncontrados([]);
            setBuscando(false);
            return undefined;
        }
        setBuscando(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const resultado = await buscarClientes(busqueda.trim());
                setEncontrados(Array.isArray(resultado) ? resultado.slice(0, 6) : []);
            } catch (error) {
                setEncontrados([]);
            } finally {
                setBuscando(false);
            }
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [busqueda]);

    const vincular = (cliente) => {
        setClienteVinculado(cliente);
        setBusqueda('');
        setEncontrados([]);
    };

    const desvincular = () => setClienteVinculado(null);

    if (clienteVinculado) {
        return (
            <div className="d-flex flex-column gap-2">
                <div
                    className="d-flex align-items-center gap-2 p-2 rounded-3"
                    style={{ background: `${color}14`, border: `1px solid ${color}55` }}
                >
                    <i className="bi bi-check-circle-fill" style={{ color }}></i>
                    <div className="flex-grow-1">
                        <div className="fw-semibold">{apellidoNombre(clienteVinculado.apellido, clienteVinculado.nombre)}</div>
                        <small className="text-muted">Cliente del estudio · {clienteVinculado.cuil}</small>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill" onClick={desvincular}>
                        Quitar
                    </button>
                </div>
                <button
                    type="button"
                    className="btn rounded-pill align-self-start text-white"
                    style={{ background: color, border: 'none' }}
                    onClick={() => onContinuar(clienteVinculado)}
                >
                    <i className="bi bi-arrow-right-circle me-1"></i>
                    Continuar con {clienteVinculado.nombre}
                </button>
            </div>
        );
    }

    return (
        <div>
            <label className="form-label fw-semibold">
                <i className="bi bi-search me-1" style={{ color }}></i>¿Generar para un cliente del sistema?
            </label>
            <input
                className="form-control"
                placeholder="Buscar por CUIL, nombre o apellido..."
                value={busqueda}
                autoComplete="off"
                onChange={(e) => setBusqueda(e.target.value)}
                style={{ borderColor: busqueda ? color : undefined }}
            />
            {buscando && <small className="text-muted d-block mt-1">Buscando...</small>}
            {!buscando && busqueda.trim() && encontrados.length === 0 && (
                <small className="text-muted d-block mt-1">Sin resultados.</small>
            )}
            {encontrados.length > 0 && (
                <ul className="list-group mt-1" style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {encontrados.map((cliente) => (
                        <li
                            key={cliente.cuil}
                            className="list-group-item list-group-item-action"
                            role="button"
                            onClick={() => vincular(cliente)}
                        >
                            {apellidoNombre(cliente.apellido, cliente.nombre)}
                            <span className="text-muted"> · {cliente.cuil}</span>
                        </li>
                    ))}
                </ul>
            )}
            <button
                type="button"
                className="btn btn-outline-secondary rounded-pill mt-3"
                style={{ color, borderColor: color }}
                onClick={() => onContinuar(null)}
            >
                Continuar sin cliente
            </button>
        </div>
    );
}
